<script setup lang="ts">
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import analytics from '@/utils/analytics';

import { BNPL_USER_CATEGORIES, waitlistBannerText } from '@/data/payments';

import Message from 'primevue/message';
import MessageDocumentRequests from '@/components/banners/MessageDocumentRequests.vue';
import TheTaxesMessageBanner from '@/components/banners/TheTaxesMessageBanner.vue';
import CustomLink from '@/components/CustomLink.vue';
import i18n from '@/plugins/i18n';
import { format } from 'date-fns';
import { LocalStorage, LocalStorageKeys } from '@/utils/local-storage';
import { SEGMENT_EVENTS } from '@/data/segment-events';
import { RouteNames } from '@/data/routes';
import { getShortBillId } from '@/utils/payments';
import { UserResponseDto } from '@clearbanc/baus-sdk';
import { DebtObligation } from '@/types/debt-obligation.types';
import { Bill } from '@/types/bill.types';
import { UserOnboardingSteps } from '@/types/onboarding.types';
import { Capacity } from '@/types/capacity.types';
import { CountryCode } from '@clearbanc/data-common-types';
import { Contract } from '@/types/contract.types';
import { BusinessDetails } from '@/types/business.types';

const store = useStore();
const router = useRouter();

const showNewUserBanner = ref(
  !!LocalStorage.getItem<boolean>(LocalStorageKeys.NEW_USER),
);

// TODO (sc-190169): These functions are temporary to allow us to have typed state.
// Once we move from Vuex to Pinia, this will no longer be necessary.
const isMissingRevenueAccountConnection = computed<boolean>(
  () => store.getters.isMissingRevenueAccountConnection,
);
const debtObligationsRequired = computed<boolean>(
  () => store.getters.debtObligationsRequired,
);
const businessDebtObligations = computed<DebtObligation | undefined>(() => {
  return store.getters.businessDebtObligations;
});
const isInFinancialDocsSegment = computed<boolean>(
  () => store.getters.isInFinancialDocsSegment,
);
const fiscalDocumentsUpToDate = computed<boolean>(
  () => store.getters.fiscalDocumentsUpToDate,
);
const fiscalStartDate = computed<Date | undefined>(
  () => store.getters.fiscalStartDate,
);
const authorizedSignatory = computed<UserResponseDto | undefined>(() => {
  return store.getters.authorizedSignatory;
});
const isAuthorizedSignatory = computed<boolean>(
  () => store.getters.isAuthorizedSignatory,
);
const businessProfileSteps = computed<UserOnboardingSteps>(() => {
  return store.getters.businessProfileSteps || {};
});
const bills = computed<Bill[]>(() => store.getters.bills || []);
const business = computed<BusinessDetails>(() => store.getters.business);
const businessCapacity = computed<Capacity>(
  () => store.getters.businessCapacity,
);
const userCategory = computed<BNPL_USER_CATEGORIES>(
  () => store.getters.userCategory,
);
const disableInvoiceUpload = computed<boolean>(
  () => store.getters.disableInvoiceUpload,
);
const showNewProductBanner = computed<boolean>(
  () => store.getters.showNewProductBanner,
);
const businessCorpCountry = computed<CountryCode>(
  () => store.getters.businessCorpCountry,
);
const userHasForceBnplFlag = computed<boolean>(
  () => store.getters.userHasForceBnplFlag,
);
const pendingContracts = computed<Contract[]>(
  () => store.getters.pendingContracts ?? [],
);
const showShopifyMessage = computed<boolean>(
  () => store.getters.showShopifyMessage,
);
const user = computed<UserResponseDto>(() => store.getters.user);

const businessCapacityApproved = computed<boolean>(
  () => store.getters.businessCapacityApproved,
);

const firstName = computed(() => {
  const { firstName } = user.value;
  if (!firstName) return '';
  return firstName.charAt(0).toUpperCase() + firstName.slice(1);
});

const authorizedSignatoryFullName = computed(() => {
  return authorizedSignatory.value?.fullName;
});

const authorizedSignatoryEmail = computed(() => {
  return authorizedSignatory.value?.email;
});

const hasSubmittedBill = computed(() => bills.value.length > 0);

const numberOfCompletedSteps = computed<number>(() =>
  Object.values(businessProfileSteps.value).reduce(
    (pv, step) => pv + (step.isComplete ? 1 : 0),
    0,
  ),
);

const numberOfSteps = computed<number>(
  () => Object.keys(businessProfileSteps.value).length,
);

const showBizProfileNotification = computed<boolean>(() => {
  const shouldShowModal =
    hasSubmittedBill.value &&
    numberOfCompletedSteps.value !== numberOfSteps.value;

  if (shouldShowModal && debtObligationsRequired.value) {
    const debtObligationsCreatedAt = businessDebtObligations.value?.created_at;
    analytics.track(SEGMENT_EVENTS.if_payment_obligations_update, {
      businessId: business.value.id,
      lastObligationsSubmissionDate: debtObligationsCreatedAt
        ? format(new Date(debtObligationsCreatedAt), 'yyyy-MM-dd')
        : 'Obligations never submitted',
      capacityId: businessCapacity.value.id,
    });
  }

  return shouldShowModal;
});

const showPaymentObligationsMessage = computed(() => {
  if (businessCapacityApproved.value) {
    return false;
  }
  if (numberOfCompletedSteps.value < numberOfSteps.value - 1) {
    return false;
  }
  return (
    (isInFinancialDocsSegment.value &&
      !fiscalDocumentsUpToDate.value &&
      !fiscalStartDate.value) ||
    debtObligationsRequired.value
  );
});

const currentPage = computed(() => router.currentRoute.value.name);

const waitListBannerContent = computed(() =>
  waitlistBannerText(
    userCategory.value,
    businessCorpCountry.value,
    userHasForceBnplFlag.value,
    bills.value.length > 0,
  ),
);

const pendingContractsLabel = computed<string>(() => {
  const { length } = pendingContracts.value;
  return i18n.t('paymentsPage.pendingContracts.primary', {
    length,
    contract: i18n.tc('paymentsPage.pendingContracts.contract', length),
  });
});

function closeNewProductBanner() {
  analytics.track(SEGMENT_EVENTS.fe_close_clicked, {
    targetArea: 'phoenix_intro_messaging',
  });
  LocalStorage.setItem(LocalStorageKeys.HIDE_PHOENIX_INTRO_BANNER, true);
}

function closeNewUserBanner() {
  LocalStorage.removeItem(LocalStorageKeys.NEW_USER);
  showNewUserBanner.value = false;
}

function handleOpenPendingContract() {
  const [firstPendingContract] = pendingContracts.value;
  if (firstPendingContract?.billId) {
    analytics.track(SEGMENT_EVENTS.if_contract_banner_clicked, {
      businessId: business.value.id,
      businessName: business.value.name,
      shortBillId: getShortBillId(firstPendingContract.billId),
      billId: firstPendingContract.billId,
      signatory: isAuthorizedSignatory.value,
    });
    router.push({
      name: RouteNames.HOME.INITIAL,
      params: { billId: firstPendingContract.billId },
    });
  }
}
</script>

<template>
  <Message v-if="pendingContracts.length" severity="warn" :closable="false">
    <div v-if="isAuthorizedSignatory">
      <h3>{{ pendingContractsLabel }}</h3>
      <p>
        <CustomLink
          :text="$t('paymentsPage.pendingContracts.completeSigning')"
          :link-text="$t('paymentsPage.pendingContracts.linkText')"
          @click="handleOpenPendingContract"
        />
      </p>
    </div>
    <h3 v-else>
      {{
        $t('paymentsPage.pendingContracts.secondary', {
          fullName: authorizedSignatoryFullName,
          email: authorizedSignatoryEmail,
          length: pendingContracts.length,
          contract: i18n.tc(
            'paymentsPage.pendingContracts.contract',
            pendingContracts.length,
          ),
        })
      }}
    </h3>
  </Message>
  <div v-if="store.getters.netNewInternationalUser">
    <Message v-if="waitListBannerContent" severity="info" :closable="false">
      <h3>
        {{ $t(waitListBannerContent.title) }}
      </h3>
      <p>
        {{ $t(waitListBannerContent.paragraph1) }}
      </p>
      <p>
        <a :href="waitListBannerContent.linkUrl" target="_blank">{{
          $t(waitListBannerContent.linkText)
        }}</a>
      </p>
    </Message>
  </div>
  <div v-else>
    <MessageDocumentRequests
      v-if="
        currentPage !== RouteNames.DOCUMENTS.INDEX &&
        currentPage !== RouteNames.BANKING.STATEMENTS &&
        currentPage !== RouteNames.FINANCIAL.DOCUMENTS
      "
    />
    <template v-if="currentPage === RouteNames.HOME.INITIAL">
      <TheTaxesMessageBanner />
      <Message
        v-if="!waitListBannerContent && disableInvoiceUpload"
        severity="info"
        :closable="false"
      >
        <h3>Expect delays.</h3>
        <p>
          We have seen a rapid influx of funding requests and we are
          unfortunately experiencing delays in our turnaround times for
          processing invoices. In order to make sure that we deliver a
          world-class experience for our founders, we are temporarily suspending
          uploads in order to best service our existing customers. Click the
          Join Waitlist button and get notified as soon as we are accepting new
          founders!
        </p>
      </Message>
      <Message
        v-if="waitListBannerContent && showNewProductBanner"
        severity="info"
        :closable="true"
        @close="closeNewProductBanner"
      >
        <h3>
          {{ $t(waitListBannerContent.title, { firstName }) }}
        </h3>
        <p v-if="waitListBannerContent.paragraph1">
          {{ $t(waitListBannerContent.paragraph1) }}
        </p>
        <p
          v-if="
            waitListBannerContent.paragraph2NoLink ||
            waitListBannerContent.paragraph2
          "
        >
          {{
            waitListBannerContent.paragraph2NoLink
              ? $t(waitListBannerContent.paragraph2NoLink)
              : $t(waitListBannerContent.paragraph2)
          }}
        </p>
        <i18n-t
          v-if="waitListBannerContent.paragraph3"
          :keypath="waitListBannerContent.paragraph3"
          tag="p"
        >
          <template #email>
            <a :href="`mailto:${$t(waitListBannerContent.supportEmail)}`">
              {{ $t(waitListBannerContent.supportEmail) }}
            </a>
          </template>
        </i18n-t>
      </Message>
      <Message
        v-if="showNewUserBanner"
        id="successful-signup-banner"
        severity="success"
        :closable="true"
        @close="closeNewUserBanner"
      >
        <h3>
          {{ $t('account.pages.pageVendors.youHaveCreatedAnAccount') }}
        </h3>
        <p>
          {{ $t('account.pages.pageVendors.getStartedNow') }}
        </p>
      </Message>
      <Message
        v-if="showBizProfileNotification"
        severity="warn"
        :closable="false"
      >
        <h3>
          {{ $t('account.pages.pageVendors.toProcessAPayment') }}
        </h3>
        <p>
          {{
            $t('paymentsPage.ofNCompleted', {
              numberOfCompletedSteps,
              numberOfSteps,
            })
          }}
        </p>
        <router-link :to="{ name: RouteNames.PROFILE.INDEX }">
          {{ $t('account.pages.pageVendors.completeProfile') }}
        </router-link>
      </Message>
      <Message
        v-if="isMissingRevenueAccountConnection"
        severity="warn"
        :closable="false"
      >
        <p>
          Please
          <router-link :to="{ name: RouteNames.CONNECTED_ACCOUNTS.INDEX }">
            Connect a revenue account
          </router-link>
          by connecting sales accounts to avoid funding delays.
        </p>
      </Message>
      <Message
        v-if="showPaymentObligationsMessage"
        severity="warn"
        :closable="false"
      >
        <h3>Update Payment Obligations</h3>
        <p>
          Please
          <router-link
            :to="{ name: store.getters.financialDetailsDestination }"
          >
            update your Payment Obligations
          </router-link>
          to avoid funding delays.
        </p>
      </Message>
      <Message v-if="showShopifyMessage" severity="info" :closable="false">
        <p>
          {{ $t('account.banners.reconnectShopifyAccounts') }}
          <router-link
            :to="{
              name: RouteNames.CONNECTED_ACCOUNTS.INDEX,
              query: { reconnect: 'shopify' },
            }"
          >
            Reconnect Shopify Account.
          </router-link>
        </p>
      </Message>
    </template>
  </div>
</template>
